import { useState, useRef, useEffect, useCallback } from 'react';

interface UseInfiniteScrollOptions<T> {
    data: T[];
    initialItemsPerLoad?: number;
    itemsPerLoad?: number;
    enabled?: boolean;
}

interface UseInfiniteScrollReturn<T> {
    displayedData: T[];
    hasMore: boolean;
    loadingMore: boolean;
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    observerTarget: React.RefObject<HTMLDivElement>;
    resetScroll: () => void;
}

export function useInfiniteScroll<T>({
    data,
    initialItemsPerLoad = 10,
    itemsPerLoad = 10,
    enabled = true,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
    const [displayedCount, setDisplayedCount] = useState(initialItemsPerLoad);
    const [loadingMore, setLoadingMore] = useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const observerTarget = useRef<HTMLDivElement>(null);

    // Reset displayed count when data changes
    useEffect(() => {
        setDisplayedCount(initialItemsPerLoad);
        // Scroll to top when data changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [data, initialItemsPerLoad]);

    // Load more items
    const loadMore = useCallback(() => {
        if (!enabled || loadingMore || displayedCount >= data.length) return;

        setLoadingMore(true);

        // Simulate loading delay for smooth UX
        setTimeout(() => {
            setDisplayedCount((prev) => Math.min(prev + itemsPerLoad, data.length));
            setLoadingMore(false);
        }, 300);
    }, [enabled, loadingMore, displayedCount, data.length, itemsPerLoad]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!enabled) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore) {
                    loadMore();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [loadMore, loadingMore, enabled]);

    const resetScroll = useCallback(() => {
        setDisplayedCount(initialItemsPerLoad);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [initialItemsPerLoad]);

    return {
        displayedData: data.slice(0, displayedCount),
        hasMore: displayedCount < data.length,
        loadingMore,
        scrollContainerRef,
        observerTarget,
        resetScroll,
    };
}